import NotesIcon from '@mui/icons-material/Notes'
import { Box, Card, CardContent, IconButton, Stack, Tooltip } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  BinIcon,
  EditIcon,
  ErrorIcon,
  FlatRackIcon,
  InfoIcon,
  WarningIcon,
  YardIcon,
} from '@tom-ui/ui'
import { BlockAccessPointDto, EquipmentTypes } from '@yard/app/api'
import { observer } from 'mobx-react-lite'
import { FC } from 'react'
import { CapacityTooltipContent } from '../atoms/CapacityTooltipContent'
import { YardNodeHeaderTitle } from '../atoms/YardNodeHeaderTitle'
import { YardNodeHeaderAccessPoints } from './YardNode/YardNodeHeaderAccessPoints'
import { YardNodeHeaderEquipmentType } from './YardNode/YardNodeHeaderEquipmentType'

type Props = {
  id: string
  name: string
  maxTiers: number
  description?: string | null
  equipmentTypes: EquipmentTypes[]
  accessPoints: BlockAccessPointDto[]
  focused: boolean
  isEditable: boolean
  hasOccupancies: boolean
  isOvercapacity: boolean
  isWarning: boolean
  utilizationPct: number
  consumedTeu: number
  totalTeu: number
  onEdit: (id: string) => void
  onDelete: (id: string) => void
  onAddAccessPoint: (blockId: string) => void
  onEditAccessPoint: (blockId: string, accessPointId: string) => void
}

export const YardBlockHeader: FC<Props> = observer(props => {
  const {
    name,
    maxTiers,
    description,
    equipmentTypes,
    accessPoints,
    focused,
    isEditable,
    id,
    hasOccupancies,
    isOvercapacity,
    isWarning,
    utilizationPct,
    consumedTeu,
    totalTeu,
    onEdit,
    onDelete,
    onAddAccessPoint,
    onEditAccessPoint,
  } = props
  const { t } = useTranslate()
  const overCapacityLabel = t('blockCapacityFull', 'Block full')
  const warningLabel = t('blockCapacityWarning', 'Block approaching capacity')
  const capacityLabel = t('blockCapacity', 'Block capacity')

  return (
    <Card>
      <CardContent sx={{ padding: '4px !important' }}>
        <Stack spacing={2} direction='row' alignItems='center' justifyContent='space-between'>
          <YardNodeHeaderTitle title={name} icon={<YardIcon />} />

          {focused && (
            <>
              <YardNodeHeaderTitle
                title={t('nTiers', '{number} tiers', { number: maxTiers })}
                icon={<FlatRackIcon />}
              />

              <YardNodeHeaderEquipmentType
                yardBlockId={id}
                equipmentTypes={equipmentTypes}
                isEditable={isEditable}
              />

              <YardNodeHeaderAccessPoints
                yardBlockId={id}
                accessPoints={accessPoints}
                isEditable={isEditable}
                onAdd={onAddAccessPoint}
                onEdit={onEditAccessPoint}
              />
              {description && (
                <Tooltip title={description}>
                  <NotesIcon sx={{ cursor: 'default', marginRight: '8px !important' }} />
                </Tooltip>
              )}
            </>
          )}

          {focused && isEditable && (
            <>
              <Tooltip title={t('edit', 'Edit')}>
                <IconButton onClick={() => onEdit(id)}>
                  <EditIcon />
                </IconButton>
              </Tooltip>
              {!hasOccupancies && (
                <Tooltip title={t('delete', 'Delete')}>
                  <IconButton onClick={() => onDelete(id)}>
                    <BinIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {!isWarning && !isOvercapacity && (
            <Tooltip
              title={
                <CapacityTooltipContent
                  headline={capacityLabel}
                  utilizationPct={utilizationPct}
                  consumedTeu={consumedTeu}
                  totalTeu={totalTeu}
                />
              }
            >
              <Box component='span' sx={{ display: 'inline-flex', mr: 1 }}>
                <InfoIcon
                  fontSize='small'
                  aria-label={capacityLabel}
                  data-cy='yard-block-capacity-info-icon'
                  sx={{ color: 'text.primary' }}
                />
              </Box>
            </Tooltip>
          )}

          {isOvercapacity && (
            <Tooltip
              title={
                <CapacityTooltipContent
                  headline={overCapacityLabel}
                  utilizationPct={utilizationPct}
                  consumedTeu={consumedTeu}
                  totalTeu={totalTeu}
                />
              }
            >
              <Box component='span' sx={{ display: 'inline-flex', mr: 1 }}>
                <ErrorIcon
                  color='error'
                  fontSize='small'
                  aria-label={overCapacityLabel}
                  data-cy='yard-block-overcapacity-icon'
                />
              </Box>
            </Tooltip>
          )}

          {isWarning && !isOvercapacity && (
            <Tooltip
              title={
                <CapacityTooltipContent
                  headline={warningLabel}
                  utilizationPct={utilizationPct}
                  consumedTeu={consumedTeu}
                  totalTeu={totalTeu}
                />
              }
            >
              <Box component='span' sx={{ display: 'inline-flex', mr: 1 }}>
                <WarningIcon
                  color='warning'
                  fontSize='small'
                  aria-label={warningLabel}
                  data-cy='yard-block-warning-icon'
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
})
